// src/pages/Dashboard.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Paper,
  Box,
  Typography,
  Button,
  useMediaQuery,
  CircularProgress,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  FaUserGraduate,
  FaChalkboardTeacher,
  FaUserTie,
  FaFileInvoice,
  FaWallet,
  FaBullhorn,
  FaBook,
  FaTasks,
  FaBoxOpen,
  FaLayerGroup,
  FaListAlt,
  FaPlus,
  FaEye,
} from "react-icons/fa";
import api from "../api";

const Dashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const [storedUser, setStoredUser] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setStoredUser({
      id: localStorage.getItem("id"),
      name: localStorage.getItem("name"),
      role: localStorage.getItem("role"),
    });
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    const schoolId = localStorage.getItem("school_id");
    if (!schoolId) {
      navigate("/login");
      return;
    }

    let aborted = false;
    const controller = new AbortController();

    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await api.post(
          "/get-data",
          { id: localStorage.getItem("id") },
          { signal: controller.signal }
        );
        const resData = res?.data?.data || {};
        if (aborted) return;
        setData({
          students: Number(resData.students || 0),
          teachers: Number(resData.teachers || 0),
          staff: Number(resData.staff || 0),
          bill: new Intl.NumberFormat("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(parseFloat(resData.bill || 0)),
          balance: new Intl.NumberFormat("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(parseFloat(resData.balance || 0)),
        });
      } catch (err) {
        console.error("Dashboard fetch error:", err);
      } finally {
        if (!aborted) setLoading(false);
      }
    };

    fetchData();
    return () => {
      aborted = true;
      controller.abort();
    };
  }, [navigate]);

  const roleIsSuper = storedUser?.role === "super_admin";

  // 👉 Sidebar menu items converted into cards
  const menuCards = [
    { id: "productCategory", label: "Add Category", icon: <FaLayerGroup />, color: "#6d4c41", routeOnCard: "/admin/product-category" },
    { id: "productSub", label: "Add Sub Category", icon: <FaBoxOpen />, color: "#5d4037", routeOnCard: "/admin/product-sub-category" },
    { id: "productAdd", label: "Add Product", icon: <FaBoxOpen />, color: "#7b1fa2", routeOnCard: "/admin/product-bundle/create" },
    { id: "productList", label: "List Product", icon: <FaListAlt />, color: "#4527a0", routeOnCard: "/admin/product-bundle/list" },

    { id: "subjectAdd", label: "Add Subject", icon: <FaBook />, color: "#2e7d32", routeOnCard: "/admin/subject-add" },
    { id: "assignsubject", label: "Assign Subject", icon: <FaTasks />, color: "#388e3c", routeOnCard: "/admin/subject-class/assign" },

    { id: "addteacher", label: "Add Teacher", icon: <FaChalkboardTeacher />, color: "#f57c00", routeOnCard: "/admin/teacher-add" },
    { id: "assignsubjecttoteacher", label: "Assign Subject to Teacher", icon: <FaUserTie />, color: "#ef6c00", routeOnCard: "/admin/teacher-subject/assign" },

    { id: "notice", label: "Notice", icon: <FaBullhorn />, color: "#c62828", routeOnCard: "/admin/notice" },
    { id: "wallet", label: "Wallet History", icon: <FaWallet />, color: "#00796b", routeOnCard: "/admin/wallet-history" },
  ];

  // 👉 Stats cards
  const statsCards = [
    {
      id: "students",
      label: "Total Students",
      icon: <FaUserGraduate />,
      color: "#1e88e5",
      valueKey: "students",
      routeOnCard: "/admin/view-student",
      routeOnButton: "/admin/add-student",
      buttonLabel: "Add Student",
      buttonIcon: <FaPlus />,
    },
    {
      id: "teachers",
      label: "Total Teachers",
      icon: <FaChalkboardTeacher />,
      color: "#43a047",
      valueKey: "teachers",
      routeOnCard: "/admin/teacher-add",
      routeOnButton: "/admin/teacher-add",
      buttonLabel: "Add Teacher",
      buttonIcon: <FaPlus />,
    },
    {
      id: "staff",
      label: "Total Staff",
      icon: <FaUserTie />,
      color: "#fb8c00",
      valueKey: "staff",
      routeOnCard: "/admin/dashboard",
      routeOnButton: "/admin/dashboard",
      buttonLabel: "Add Staff",
      buttonIcon: <FaPlus />,
    },
    {
      id: "billing",
      label: "Total Billing",
      icon: <FaFileInvoice />,
      color: "#8e24aa",
      valueKey: "bill",
      routeOnCard: roleIsSuper ? "/admin/all-billing" : "/admin/student-billing",
      routeOnButton: "/admin/student-billing",
      buttonLabel: "New Bill",
      buttonIcon: <FaPlus />,
    },
    {
      id: "wallet",
      label: roleIsSuper ? "Admin Wallet" : "Wallet",
      icon: <FaWallet />,
      color: "#00796b",
      valueKey: "balance",
      routeOnCard: roleIsSuper ? "/admin/wallet" : "/admin/staff/wallet",
      routeOnButton: "/admin/wallet-history",
      buttonLabel: "Wallet History",
      buttonIcon: <FaEye />,
    },
  ];

  const readValue = (key) => {
    if (!data) return "-";
    if (key === "bill" || key === "balance") return `₹ ${data[key]}`;
    return data[key] ?? 0;
  };

  return (
    <Box>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 260,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Stats Cards */}
          <Typography sx={{ fontWeight: 800, mb: 2 }}>Overview</Typography>
          <Grid container spacing={3}>
            {statsCards.map((card) => (
              <Grid item xs={12} sm={6} md={4} key={card.id}>
                <Paper
                  elevation={3}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 2,
                    borderRadius: 2,
                    minHeight: 110,
                    transition: "transform .18s ease, box-shadow .18s ease",
                    "&:hover": { transform: "translateY(-6px)", boxShadow: 8 },
                    cursor: "pointer",
                  }}
                  onClick={() => navigate(card.routeOnCard)}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      display: "grid",
                      placeItems: "center",
                      background: `${card.color}22`,
                    }}
                  >
                    <Box sx={{ color: card.color, fontSize: 28 }}>{card.icon}</Box>
                  </Box>
                  <Box sx={{ flex: 1, px: 2 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 14, color: "text.secondary" }}>
                      {card.label}
                    </Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: 22 }}>
                      {readValue(card.valueKey)}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(card.routeOnButton);
                    }}
                    startIcon={card.buttonIcon}
                    sx={{
                      background: `linear-gradient(90deg, ${card.color}, ${card.color}cc)`,
                      color: "white",
                      fontWeight: 700,
                      borderRadius: 2,
                      px: 1.5,
                      py: 0.8,
                    }}
                  >
                    {card.buttonLabel}
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 4 }} />

          {/* Menu Cards (Sidebar items) */}
          <Typography sx={{ fontWeight: 800, mb: 2 }}>All Menus</Typography>
          <Grid container spacing={3}>
            {menuCards.map((m) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={m.id}>
                <Paper
                  elevation={2}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 3,
                    borderRadius: 2,
                    textAlign: "center",
                    transition: "all .2s ease",
                    "&:hover": { transform: "translateY(-5px)", boxShadow: 6 },
                    cursor: "pointer",
                  }}
                  onClick={() => navigate(m.routeOnCard)}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      background: `${m.color}22`,
                      mb: 1.5,
                    }}
                  >
                    <Box sx={{ color: m.color, fontSize: 28 }}>{m.icon}</Box>
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
                    {m.label}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default Dashboard;
